/**
 * Bulk registration — review the batch before paying.
 *
 * Prices come from POST /teams/{id}/register-batch/preview (the same quote
 * the TourneyNation calendar cart uses): each event's fee, the batch's
 * discounts (the larger of the event's own discount and the organizer's
 * multi-event discount, per event), and the total. "Continue to payment"
 * creates the registrations, then the Payment step charges them once.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { EmbedClient, BatchPreview } from '@/api/embed';
import { SaveTeamOption } from '@/components/SaveTeamOption';
import { useFlow } from '@/state/FlowContext';
import { money, shortDate } from './bulkFormat';
import './Bulk.css';
import './ConfirmPage.css';

interface Props {
  client: EmbedClient;
}

export function BulkReviewPage({ client }: Props) {
  const navigate = useNavigate();
  const {
    selectedTeam, bulkEvents, bulkDivisions, bulkAnswers, bulkBatch, setBulkBatch,
    createAccount, setCreateAccount,
  } = useFlow();
  // A guest or new email has no TourneyNation account yet: offer to save
  // their team (the account is created on the done screen after payment).
  const [noAccount, setNoAccount] = useState(false);
  useEffect(() => {
    client.getMe().then((me) => setNoAccount(me.account_status === 'stub')).catch(() => { /* non-fatal */ });
  }, [client]);
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [busy, setBusy] = useState<null | 'card' | 'pay_later'>(null);
  const [error, setError] = useState<string | null>(null);

  const items = bulkEvents.map((e) => ({ event_id: e.id, division: bulkDivisions[e.id] }));

  useEffect(() => {
    if (!selectedTeam || bulkEvents.length === 0) {
      navigate('/bulk/events', { replace: true });
      return;
    }
    client
      .previewBatch(selectedTeam.id, items)
      .then(setPreview)
      .catch(() => setError('Could not price these events. Please try again.'));
    // items derives from the two context values below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, selectedTeam, bulkEvents, bulkDivisions, navigate]);

  if (!selectedTeam) return null;

  const onContinue = async (method: 'card' | 'pay_later' = 'card') => {
    setBusy(method);
    setError(null);
    try {
      const withAnswers = items.map((i) => (bulkAnswers[i.event_id] ? { ...i, answers: bulkAnswers[i.event_id] } : i));
      if (method === 'pay_later') {
        // Always asks the server: registrations made by an earlier "Pay now"
        // (then Back) are picked back up and held for paying later.
        const batch = await client.registerBatch(selectedTeam.id, withAnswers, { paymentMethod: 'pay_later' });
        setBulkBatch(batch);
        navigate('/bulk/done?pay_later=1');
        return;
      }
      // Back from Payment and continue again: the registrations already
      // exist (changing events or divisions clears the stored batch).
      const batch = bulkBatch ?? await client.registerBatch(selectedTeam.id, withAnswers);
      setBulkBatch(batch);
      const needsPayment = batch.registrations.some((r) => r.status === 'pending_payment' && r.amount_cents > 0);
      navigate(needsPayment ? '/bulk/payment' : '/bulk/done');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not register. Please try again.');
      setBusy(null);
    }
  };

  // The API reports the card total: show the real charge, not just fees.
  const allIn = preview?.card_total_cents != null;
  const laterCount = preview
    ? preview.items.filter((it) => it.pays_now === false).length
    : bulkEvents.filter((e) => e.registration_mode === 'approval_required').length;
  // Pay later, known from the event list before anything is priced: every
  // tournament charged now must allow it (approval ones are paid later
  // anyway), and there must be one.
  const paidNow = bulkEvents.filter((e) => e.registration_mode !== 'approval_required');
  const payLaterOk = paidNow.length > 0 && paidNow.every((e) => e.pay_later_enabled);
  const lineFor = (eventId: string) => preview?.items.find((it) => it.event_id === eventId);

  return (
    <div className="tn-bulk">
      <h1 className="tn-bulk__title">
        Review {bulkEvents.length} registration{bulkEvents.length === 1 ? '' : 's'}
      </h1>

      {/* Shown at once from the picks; prices fill in from the preview
          (usually already fetched while divisions were being chosen). */}
      <dl className="tn-bulk__receipt">
        {bulkEvents.map((ev) => {
          const it = lineFor(ev.id);
          const later = it ? it.pays_now === false : ev.registration_mode === 'approval_required';
          const sub = [
            bulkDivisions[ev.id],
            shortDate(ev.event_date, ev.ends_at),
            later ? 'Charged once approved' : null,
          ].filter(Boolean).join(' · ');
          return (
            <div key={ev.id} className="tn-bulk__receipt-row">
              <dt>
                <span className="tn-bulk__receipt-main">{ev.name}</span>
                {sub ? <span className="tn-bulk__receipt-sub">{sub}</span> : null}
              </dt>
              <dd className={it ? undefined : 'tn-bulk__pending'}>{it ? money(it.fee_cents) : '…'}</dd>
            </div>
          );
        })}
        {preview && preview.discount_cents > 0 ? (
          <div className="tn-bulk__receipt-row tn-bulk__receipt-row--discount">
            <dt>Discounts</dt>
            <dd>−{money(preview.discount_cents)}</dd>
          </div>
        ) : null}
        {preview && preview.credit_cents > 0 ? (
          <div className="tn-bulk__receipt-row tn-bulk__receipt-row--discount">
            <dt>Account credit</dt>
            <dd>−{money(preview.credit_cents)}</dd>
          </div>
        ) : null}
        {/* The real charge, as the payment step makes it: entry fees plus the
            platform fee and card processing (an API without these numbers
            falls back to the entry fees and the note below). */}
        {(preview?.platform_fee_cents ?? 0) > 0 ? (
          <div className="tn-bulk__receipt-row tn-bulk__receipt-row--fee">
            <dt>Platform fee</dt>
            <dd>{money(preview?.platform_fee_cents ?? 0)}</dd>
          </div>
        ) : null}
        {(preview?.processing_fee_cents ?? 0) > 0 ? (
          <div className="tn-bulk__receipt-row tn-bulk__receipt-row--fee">
            <dt>Card processing</dt>
            <dd>{money(preview?.processing_fee_cents ?? 0)}</dd>
          </div>
        ) : null}
        <div className="tn-bulk__receipt-row tn-bulk__receipt-row--total">
          <dt>{allIn && laterCount ? 'Total today' : 'Total'}</dt>
          <dd className={preview ? undefined : 'tn-bulk__pending'}>
            {preview ? money(allIn ? preview.card_total_cents ?? 0 : preview.total_cents) : '…'}
          </dd>
        </div>
      </dl>

      <p className="tn-bulk__fine">
        {!preview ? '' : allIn
          ? laterCount
            ? `${laterCount === 1 ? 'The tournament that needs' : 'Tournaments that need'} organizer approval ${laterCount === 1 ? 'is' : 'are'} charged once approved.`
            : ''
          : 'Card payments may add a platform fee and a processing fee, shown itemized before you pay.'}
        {preview && preview.discount_cents > 0
          ? ' If you cancel an event later, the multi-event discount on your other events is adjusted.'
          : ''}
      </p>

      {noAccount ? <SaveTeamOption checked={createAccount} onChange={setCreateAccount} /> : null}

      {error ? <div role="alert" className="tn-bulk__error">{error}</div> : null}

      <div className="tn-bulk__actions">
        {!preview ? (
          // Still pricing: hold the ways to pay rather than guess a total.
          <button type="button" className="tn-btn tn-btn--primary" disabled>
            {error ? 'Payment options unavailable' : 'Grabbing payment options…'}
          </button>
        ) : payLaterOk ? (
          <div className="tn-confirm__ways" role="group" aria-label="How to pay">
            <div className="tn-confirm__way">
              <button type="button" className="tn-btn tn-btn--primary" disabled={!!busy} onClick={() => onContinue('card')}>
                {busy === 'card' ? 'Registering…' : allIn ? `Pay now · ${money(preview.card_total_cents ?? 0)}` : 'Pay now'}
              </button>
              <p className="tn-confirm__way-fine">By card. The total above includes every fee.</p>
            </div>
            <span className="tn-confirm__or">or</span>
            <div className="tn-confirm__way">
              <button type="button" className="tn-btn tn-btn--secondary" disabled={!!busy} onClick={() => onContinue('pay_later')}>
                {busy === 'pay_later' ? 'Registering…' : 'Pay later'}
              </button>
              <p className="tn-confirm__way-fine">
                Hold your spots now and pay each entry fee by card before its tournament. We&rsquo;ll email you how.
                {noAccount ? ' Your team is saved to a TourneyNation account with this email so you can pay from My Teams.' : ''}
              </p>
            </div>
          </div>
        ) : (
          <button type="button" className="tn-btn tn-btn--primary" disabled={!!busy} onClick={() => onContinue('card')}>
            {busy ? 'Registering…' : 'Continue to payment'}
          </button>
        )}
        <button type="button" className="tn-btn tn-btn--ghost" disabled={!!busy} onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    </div>
  );
}
